import React, { useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Dispatch, Action } from 'redux';
import ActionButton from '@westwing/ui-kit/ActionButton';
import useTranslator from '../../../hooks/useTranslator';
import StateInterface, { BellaThunkDispatch } from '../../../redux/types';
import { hideOverlay } from '../../../redux/overlays/actions';
import { OverlayId } from '../types';
import useListingPageUrlState from '../../../hooks/ListingPage/useListingPageUrlState';
import { ProductFilters, AppliedFilter, ListingPageUrlState } from '../../../redux/products/types';
import useGa from '../../../hooks/useGa';
import Flyout from '../GenericFlyout';
import ErrorBoundary from '../../Generic/ErrorBoundaries';
import { BellaTestIds } from '../../../../common/qaIds';
import FilterGroup from './FiltersGroup';
import { getFiltersForPageType } from './filterValueService';

export const OVERLAY_ID = OverlayId.FILTERS_FLYOUT;

const StyledFiltersContainer = styled.div`
    padding: 10px 30px;
`;

const MarginActionButton = styled(ActionButton)`
    margin-top: 10px;
`;

interface Props {
    hideNavigationFlyout: () => void;
    filters: ProductFilters[];
}

export type FiltersContentProps = Props;
export type FilterUrlsHydrator = (
    filters: ProductFilters[],
    urlState: ListingPageUrlState,
    appliedFilters: AppliedFilter[]
) => ProductFilters[];

export const generateGaLabel = (newListingState: AppliedFilter[], filters: ProductFilters[]) => {
    const filtersLabelsMap = filters.reduce(
        (acc, { name, displayName: label }) => ({
            ...acc,
            [name]: label,
        }),
        {}
    );

    return newListingState
        .map(filter => {
            const labelPrefix = filtersLabelsMap[filter.name];
            const joinedVals = filter.values.join(',');
            return `${labelPrefix} - ${joinedVals}`;
        })
        .join('; ');
};

const emptyFiltersState = {
    appliedFilters: [],
};

const FiltersContent: React.FC<FiltersContentProps> = ({ filters, hideNavigationFlyout }) => {
    const [urlState, setListingPageState] = useListingPageUrlState();
    const t = useTranslator();
    const { appliedFilters, listingPageType } = urlState;
    const [didInvalidate, setDidInvalidate] = useState(false);
    const gaTracking = useGa();

    const onFilterValueChange = (): void => {
        setDidInvalidate(true);
    };

    const completeFiltering = () => {
        if (didInvalidate) {
            gaTracking.trackFilters({
                action: gaTracking.actions.Filters.FilterActivated,
                label: generateGaLabel(appliedFilters, filters),
            });
        }

        hideNavigationFlyout();
    };

    const clearFilters = () => {
        setDidInvalidate(false);
        setListingPageState(emptyFiltersState);
        hideNavigationFlyout();
    };

    const createFiltersNavItems = (): React.ReactElement[] =>
        getFiltersForPageType(filters, listingPageType).map(group => (
            <ErrorBoundary key={group.name} boundaryName={`Filters Flyout ${group.name}`} fallbackComponent={null}>
                <FilterGroup key={group.name} group={group} handleValueChange={onFilterValueChange} />
            </ErrorBoundary>
        ));

    return (
        <Flyout identifier={OVERLAY_ID} title={t('')}>
            <StyledFiltersContainer>{createFiltersNavItems()}</StyledFiltersContainer>
            <StyledFiltersContainer>
                <ActionButton
                    data-testid={BellaTestIds.filtersResetBtn}
                    kind="secondary"
                    name="clear"
                    onClick={clearFilters}
                >
                    {t('Reset filter')}
                </ActionButton>
                <MarginActionButton
                    data-testid={BellaTestIds.filtersApplyBtn}
                    kind="primary"
                    name="submit"
                    onClick={completeFiltering}
                >
                    {t('Apply filters')}
                </MarginActionButton>
            </StyledFiltersContainer>
        </Flyout>
    );
};

const mapStateToProps = (state: StateInterface): { filters: ProductFilters[] } => {
    const { filters } = state.products;
    return { filters };
};

const mapDispatchToProps = (dispatch: Dispatch & BellaThunkDispatch) => ({
    hideNavigationFlyout: (): Action => dispatch(hideOverlay(OVERLAY_ID)),
});

const connected = connect(mapStateToProps, mapDispatchToProps)(FiltersContent);

export default connected;
